import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  AttendanceGridItem,
  DayStatus,
} from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventAttendences/types";

interface AttendeeDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  item: AttendanceGridItem | null;
  dayStatus: DayStatus | null;
  isFinalized: boolean;
  onSaveNotes: (attendanceId: number, notes: string) => Promise<void>;
}

export default function AttendeeDetailDrawer({
  open,
  onClose,
  item,
  dayStatus,
  isFinalized,
  onSaveNotes,
}: AttendeeDetailDrawerProps) {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (open && dayStatus) {
      setNotes(dayStatus.notes || "");
      setDirty(false);
    }
  }, [open, dayStatus]);

  if (!item || !dayStatus) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveNotes(dayStatus.attendanceId, notes);
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet onOpenChange={(o) => !o && onClose()} open={open}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-base font-normal text-gray-900 dark:text-white">
            {item.attendee?.fullName || "Unknown"}
          </SheetTitle>
          <SheetDescription className="text-sm text-gray-500 dark:text-gray-400">
            {[item.attendee?.email, item.companyName]
              .filter(Boolean)
              .join(" · ") || "No details"}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 block">
            Notes
          </label>
          {isFinalized ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {dayStatus.notes || "No notes"}
            </p>
          ) : (
            <>
              <Textarea
                className="min-h-[120px] text-sm"
                disabled={saving}
                onChange={(e) => {
                  setNotes(e.target.value);
                  setDirty(true);
                }}
                placeholder="Add notes for this day..."
                value={notes}
              />
              {dirty && (
                <div className="flex justify-end gap-2 mt-2">
                  <Button
                    disabled={saving}
                    onClick={() => {
                      setNotes(dayStatus.notes || "");
                      setDirty(false);
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    Cancel
                  </Button>
                  <Button disabled={saving} onClick={handleSave} size="sm">
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
